/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */

import React from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Icons } from '../../../../../Shared/Constants/Icons';
import { styles } from './ModifyServices.Style';

interface ITaskListProps {
  tasks: any[];
  expandedTask: string | null;
  handleToggle: (taskId: string) => void;
  handleAddSubTaskClick: (task: any) => void;
}

const TaskList: React.FC<ITaskListProps> = ({ tasks, expandedTask, handleToggle, handleAddSubTaskClick }) => {
  const { t } = useTranslation(['taskManagement', 'altTexts']);

  const renderAddSubTaskButton = (task: any) => (
    <Box sx={styles.addButton} onClick={() => handleAddSubTaskClick(task)}>
      <Box sx={styles.addButtonText}>
        <img src={Icons.AddIcon} width={20} height={20} alt={t('altTexts:add')} />
        {t('taskManagement:AddSubTask')}
      </Box>
    </Box>
  );

  const renderSubTasksList = (task: any) => (
    <>
      {task?.subTasks?.map((sub: any) => (
        <Box key={sub.woScheduleServiceId} sx={styles.subTaskItem}>
          <Typography sx={{ color: 'grey.800', fontSize: '0.9rem' }}>{sub.mdTaskName}</Typography>
        </Box>
      ))}
      {renderAddSubTaskButton(task)}
    </>
  );

  const renderExpandedTaskPanel = (task: any) => (
    <Box sx={styles.expandedTaskPanel}>
      {task.subTasks?.length > 0 ? renderSubTasksList(task) : renderAddSubTaskButton(task)}
    </Box>
  );

  const renderTask = (task: any) => {
    const isExpanded = expandedTask === task.woScheduleServiceId;

    return (
      <Box
        key={task.woScheduleServiceId}
        data-task-container="true"
        onClick={() => handleToggle(task.woScheduleServiceId)}
        sx={styles.taskContainer}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography sx={{ color: 'grey.900' }}>{task.mdTaskName}</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, padding: '8px' }}>
            <img src={isExpanded ? Icons.DropdownDownIcon : Icons.DropdownUpIcon} alt={isExpanded ? 'down' : 'up'} />
          </Box>
        </Box>
        {isExpanded && renderExpandedTaskPanel(task)}
      </Box>
    );
  };

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4, mb: 2, alignItems: 'flex-start' }}>
      {tasks?.map(renderTask)}
    </Box>
  );
};

export default TaskList;
